import { Button } from '@design/components/button/Button';
import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import {
  SmartTableColumn,
  SmartTableDataItem,
} from '@design/components/smart-table';
import { Tooltip, TooltipTrigger } from '@design/components/tooltip/Tooltip';
import { useTranslation } from 'react-i18next';
import { ConnectionsEmptyOrError } from '../connections-error-or-empty/ConnectionsEmptyOrError';
import { SharedTable } from '../shared-table/SharedTable';
import styles from './SectionBase.module.scss';

export { ConnectionsEmptyOrError } from '../connections-error-or-empty/ConnectionsEmptyOrError';

export interface SectionBaseProps<T> {
  title: string;
  onExpand?: () => void;
  tableData?: SmartTableDataItem<T>[];
  tableColumns?: SmartTableColumn<T>[];
  isFetching: boolean;
  isEmpty?: boolean;
  isError?: boolean;
  emptyTitle?: string;
  onRowClick?: (item: SmartTableDataItem<T>) => void;
  hideExpandButton?: boolean;
}

export const SectionBase = <T extends {}>({
  title,
  tableData = [],
  tableColumns = [],
  onExpand,
  isFetching,
  isEmpty = false,
  isError = false,
  emptyTitle,
  onRowClick,
  hideExpandButton = false,
}: SectionBaseProps<T>) => {
  const { t } = useTranslation(['insights']);

  return (
    <div className={styles.root}>
      <div className={styles.headerWrapper}>
        <div className={styles.title}>{title}</div>
        {!hideExpandButton && !isError && !isEmpty && (
          <Tooltip
            label={t('insights:viewMore')}
            inPortal
            trigger={[TooltipTrigger.Hover]}
          >
            <Button
              variant="tertiary"
              iconName="Expand"
              size="small"
              onClick={() => onExpand?.()}
            />
          </Tooltip>
        )}
      </div>
      {isFetching ? (
        <div className={styles.loadingSkeletonWrapper}>
          <SkeletonLoader
            areas={[
              {
                styles: { width: '100%', height: '96px', borderRadius: '8px' },
              },
            ]}
          />
        </div>
      ) : isError ? (
        <ConnectionsEmptyOrError variant="error" />
      ) : isEmpty ? (
        <ConnectionsEmptyOrError variant="empty" title={emptyTitle} />
      ) : (
        <SharedTable
          tableData={tableData}
          tableColumns={tableColumns}
          onRowClick={onRowClick}
        />
      )}
    </div>
  );
};
